import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../../firebase/config";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";
import { enviarPresupuesto } from "../../../integrations/gmailCentralizado";

const ESTADOS = {
  borrador: { label: 'Borrador', color: '#AEB6C4', bg: 'rgba(174,182,196,0.15)' },
  enviado: { label: 'Enviado', color: '#00B8FF', bg: 'rgba(0,184,255,0.15)' },
  aceptado: { label: 'Aceptado', color: '#00D084', bg: 'rgba(0,208,132,0.15)' },
  rechazado: { label: 'Rechazado', color: '#FF4757', bg: 'rgba(255,71,87,0.15)' },
};

export default function DetallePresupuesto() {
  const { id } = useParams();
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const [presupuesto, setPresupuesto] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});

  const canChangeState = ['gestion_general', 'administracion', 'recepcion'].includes(userRole);
  const canEdit = ['gestion_general', 'administracion'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `0.5px solid ${theme.border}` };

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'presupuestos', id), snap => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setPresupuesto(data);
        setForm({
          detalle: data.detalle || '',
          montoContadoARS: data.montoContadoARS || '',
          montoContadoUSD: data.montoContadoUSD || '',
          montoFinanciadoARS: data.montoFinanciadoARS || '',
          montoFinanciadoUSD: data.montoFinanciadoUSD || '',
          detalleFinanciacion: data.detalleFinanciacion || '',
          observaciones: data.observaciones || '',
          incluirEstudios: data.incluirEstudios || false,
          incluirTratamientos: data.incluirTratamientos || false,
          incluirCirugias: data.incluirCirugias || false,
          pacienteEmail: data.pacienteEmail || '',
          pacienteTelefono: data.pacienteTelefono || '',
        });
      }
    });
    return unsub;
  }, [id]);

  async function cambiarEstado(nuevoEstado) {
    await updateDoc(doc(db, 'presupuestos', id), { estado: nuevoEstado, [`fecha_${nuevoEstado}`]: new Date() });
    await registrarAccion(currentUser?.email, userRole, 'Presupuestos', `Estado: ${nuevoEstado}`, presupuesto.pacienteNombre, clinicaId);
  }

  async function guardarEdicion() {
    setEditando(true);
    try {
      await updateDoc(doc(db, 'presupuestos', id), {
        ...form,
        montoContadoARS: form.montoContadoARS ? parseFloat(form.montoContadoARS) : null,
        montoContadoUSD: form.montoContadoUSD ? parseFloat(form.montoContadoUSD) : null,
        montoFinanciadoARS: form.montoFinanciadoARS ? parseFloat(form.montoFinanciadoARS) : null,
        montoFinanciadoUSD: form.montoFinanciadoUSD ? parseFloat(form.montoFinanciadoUSD) : null,
        editadoEn: new Date(), editadoPor: currentUser?.email,
        estado: 'borrador',
      });
      await registrarAccion(currentUser?.email, userRole, 'Presupuestos', 'Editar presupuesto', presupuesto.pacienteNombre, clinicaId);
      setModalEditar(false);
    } catch (e) { alert('Error al guardar: ' + e.message); }
    setEditando(false);
  }

  async function handleEnviar() {
    if (!presupuesto.pacienteEmail) { alert('El paciente no tiene email registrado'); return; }
    setEnviando(true);
    try {
      await enviarPresupuesto(presupuesto, clinicaData);
      await cambiarEstado('enviado');
      alert('Presupuesto enviado correctamente');
    } catch (e) { alert('Error al enviar: ' + e.message); }
    setEnviando(false);
  }

  async function handleEditarYEnviar() {
    await guardarEdicion();
    await handleEnviar();
  }

  if (!presupuesto) return <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }}>Cargando...</div>;

  const estado = ESTADOS[presupuesto.estado] || { label: presupuesto.estado, color: theme.secondary, bg: theme.bgCard };
  const conceptos = [presupuesto.incluirEstudios && 'Estudios diagnósticos', presupuesto.incluirTratamientos && 'Tratamientos', presupuesto.incluirCirugias && 'Cirugías'].filter(Boolean);
  const fecha = presupuesto.creadoEn?.toDate ? presupuesto.creadoEn.toDate().toLocaleDateString('es-AR') : '—';

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Detalle de presupuesto" backLabel="Presupuestos" backRuta="/presupuestos" />

      <div style={{ padding: '12px 24px 0', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: theme.bg, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '99px', fontWeight: '500', background: estado.bg, color: estado.color }}>{estado.label}</span>
        {canEdit && (
          <button onClick={() => setModalEditar(true)} style={btnSecondaryStyle}>Editar</button>
        )}
        {canChangeState && (presupuesto.estado === 'borrador' || presupuesto.estado === 'enviado') && (
          <button onClick={handleEnviar} disabled={enviando} style={btnPrimaryStyle}>
            {enviando ? 'Enviando...' : presupuesto.estado === 'enviado' ? 'Reenviar por correo' : 'Enviar por correo'}
          </button>
        )}
        {canChangeState && presupuesto.estado === 'enviado' && (
          <>
            <button onClick={() => cambiarEstado('aceptado')} style={btnPrimaryStyle}>Marcar aceptado</button>
            <button onClick={() => cambiarEstado('rechazado')} style={{ ...btnSecondaryStyle, color: theme.error, borderColor: theme.error }}>Marcar rechazado</button>
          </>
        )}
      </div>

      <div style={{ padding: '24px', background: theme.bg, display: 'flex', flexDirection: 'column', gap: '16px' }}>

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
          <div style={{ fontSize: '11px', color: theme.accent, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>Datos del paciente</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
            {[['PACIENTE', presupuesto.pacienteNombre], ['DNI', presupuesto.pacienteDni || '—'], ['EMAIL', presupuesto.pacienteEmail || '—'], ['TELÉFONO', presupuesto.pacienteTelefono || '—'], ['FECHA', fecha], ['CREADO POR', presupuesto.creadoPor]].map(([label, valor]) => (
              <div key={label}>
                <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '4px', fontWeight: '500', letterSpacing: '1px' }}>{label}</div>
                <div style={{ fontSize: '13px', color: theme.primary, fontWeight: '500' }}>{valor}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
          <div style={{ fontSize: '11px', color: theme.accent, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>Conceptos incluidos</div>
          {conceptos.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {conceptos.map(c => <span key={c} style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '99px', background: theme.infoBg, color: theme.accent, fontWeight: '500' }}>{c}</span>)}
            </div>
          )}
          {presupuesto.detalle && <div style={{ fontSize: '13px', color: theme.primary, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{presupuesto.detalle}</div>}
        </div>

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
          <div style={{ fontSize: '11px', color: theme.accent, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>Opciones de precio</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: presupuesto.detalleFinanciacion ? '16px' : '0' }}>
            {presupuesto.montoContadoARS && <div style={{ background: theme.successBg, padding: '12px 16px', borderRadius: '10px' }}><div style={{ fontSize: '11px', color: theme.success, marginBottom: '4px', fontWeight: '500' }}>CONTADO ARS</div><div style={{ fontSize: '20px', fontWeight: '700', color: theme.success }}>${parseFloat(presupuesto.montoContadoARS).toLocaleString('es-AR')}</div></div>}
            {presupuesto.montoContadoUSD && <div style={{ background: theme.infoBg, padding: '12px 16px', borderRadius: '10px' }}><div style={{ fontSize: '11px', color: theme.accent, marginBottom: '4px', fontWeight: '500' }}>CONTADO USD</div><div style={{ fontSize: '20px', fontWeight: '700', color: theme.accent }}>USD {parseFloat(presupuesto.montoContadoUSD).toFixed(2)}</div></div>}
            {presupuesto.montoFinanciadoARS && <div style={{ background: theme.warningBg, padding: '12px 16px', borderRadius: '10px' }}><div style={{ fontSize: '11px', color: theme.warning, marginBottom: '4px', fontWeight: '500' }}>FINANCIADO ARS</div><div style={{ fontSize: '20px', fontWeight: '700', color: theme.warning }}>${parseFloat(presupuesto.montoFinanciadoARS).toLocaleString('es-AR')}</div></div>}
            {presupuesto.montoFinanciadoUSD && <div style={{ background: theme.warningBg, padding: '12px 16px', borderRadius: '10px' }}><div style={{ fontSize: '11px', color: theme.warning, marginBottom: '4px', fontWeight: '500' }}>FINANCIADO USD</div><div style={{ fontSize: '20px', fontWeight: '700', color: theme.warning }}>USD {parseFloat(presupuesto.montoFinanciadoUSD).toFixed(2)}</div></div>}
          </div>
          {presupuesto.detalleFinanciacion && (
            <div style={{ background: theme.bg, padding: '10px 14px', borderRadius: '8px', border: `0.5px solid ${theme.border}`, fontSize: '13px', color: theme.secondary }}>
              {presupuesto.detalleFinanciacion}
            </div>
          )}
        </div>

        {presupuesto.observaciones && (
          <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
            <div style={{ fontSize: '11px', color: theme.secondary, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '8px' }}>Observaciones</div>
            <div style={{ fontSize: '13px', color: theme.primary, lineHeight: '1.6' }}>{presupuesto.observaciones}</div>
          </div>
        )}

        {presupuesto.estado === 'aceptado' && (
          <div style={{ background: theme.successBg, border: `0.5px solid ${theme.success}`, borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>✅</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: theme.success }}>Presupuesto aceptado</div>
              <div style={{ fontSize: '12px', color: theme.success, opacity: 0.8 }}>{presupuesto.fecha_aceptado?.toDate ? presupuesto.fecha_aceptado.toDate().toLocaleDateString('es-AR') : ''}</div>
            </div>
          </div>
        )}
      </div>

      {/* MODAL EDITAR */}
      {modalEditar && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Editar presupuesto</span>
              <button onClick={() => setModalEditar(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>EMAIL DEL PACIENTE</label>
                <input value={form.pacienteEmail} onChange={e => setForm({...form, pacienteEmail: e.target.value})} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>TELÉFONO DEL PACIENTE</label>
                <input value={form.pacienteTelefono} onChange={e => setForm({...form, pacienteTelefono: e.target.value})} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {[['incluirEstudios', 'Estudios diagnósticos'], ['incluirTratamientos', 'Tratamientos'], ['incluirCirugias', 'Cirugías']].map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: theme.secondary, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form[key] || false} onChange={e => setForm({...form, [key]: e.target.checked})} />
                    {label}
                  </label>
                ))}
              </div>
              <div>
                <label style={labelStyle}>DETALLE DEL PRESUPUESTO</label>
                <textarea value={form.detalle} onChange={e => setForm({...form, detalle: e.target.value})} rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={labelStyle}>CONTADO ARS</label><input type="number" value={form.montoContadoARS} onChange={e => setForm({...form, montoContadoARS: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>CONTADO USD</label><input type="number" value={form.montoContadoUSD} onChange={e => setForm({...form, montoContadoUSD: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>FINANCIADO ARS</label><input type="number" value={form.montoFinanciadoARS} onChange={e => setForm({...form, montoFinanciadoARS: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>FINANCIADO USD</label><input type="number" value={form.montoFinanciadoUSD} onChange={e => setForm({...form, montoFinanciadoUSD: e.target.value})} style={inputStyle} /></div>
              </div>
              <div>
                <label style={labelStyle}>DETALLE DE FINANCIACIÓN</label>
                <input value={form.detalleFinanciacion} onChange={e => setForm({...form, detalleFinanciacion: e.target.value})} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>OBSERVACIONES</label>
                <textarea value={form.observaciones} onChange={e => setForm({...form, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
              <button onClick={guardarEdicion} disabled={editando} style={btnPrimaryStyle}>
                {editando ? 'Guardando...' : 'Guardar cambios'}
              </button>
              <button onClick={handleEditarYEnviar} disabled={editando || enviando} style={{ ...btnPrimaryStyle, background: `linear-gradient(135deg, ${theme.success}, #00A86B)` }}>
                Guardar y enviar
              </button>
              <button onClick={() => setModalEditar(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}